import React, { useState } from "react";

import { Edi834MemberCoverage, EdiEntity } from "../types";
import { formatDisplayDate, formatInstant } from "../../../helpers/dataHelper";
import { CollapsibleSection } from "../CollapsibleSection";
import { formatAddress, formatEntityName } from "../viewerHelpers";
import Field from "./Field";
import StatusBadge from "./StatusBadge";
import ExpandCollapseToggle from "./ExpandCollapseToggle";

const MemberCoverage834Viewer: React.FC<{ coverage: Edi834MemberCoverage }> = ({
  coverage,
}) => {
  const [allExpanded, setAllExpanded] = useState(true);
  const member = coverage.member;

  return (
    <div className="flex flex-col h-full w-full">
      {/* Header */}
      <div className="bg-white px-4 py-3">
        <div className="flex items-center flex-wrap gap-2">
          <StatusBadge status="MEMBER_COVERAGE" type="type" />
          <div className="flex-1" />
          <div>
            <span className="text-gray-500 text-sm">MEMBER ID</span>
            <span className="ml-2 font-medium text-sm">
              {coverage.identifier || "-"}
            </span>
          </div>
          <div>
            <span className="text-gray-500 text-sm">GROUP #</span>
            <span className="ml-2 font-medium text-sm">
              {coverage.groupOrPolicyNumber || "-"}
            </span>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <ExpandCollapseToggle
            expanded={allExpanded}
            onToggle={setAllExpanded}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 scrollbar-thin">
        <CollapsibleSection title="Transaction Info" defaultOpen={allExpanded}>
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            }}
          >
            <Field
              label="Control Number"
              value={coverage.transactionControlNumber}
            />
            <Field
              label="Purpose Code"
              value={coverage.transactionSetPurposeCode}
            />
            <Field label="Action Code" value={coverage.transactionActionCode} />
            <Field
              label="Master Policy #"
              value={coverage.masterPolicyNumber}
            />
            <Field
              label="Creation DateTime"
              value={formatInstant(coverage.transactionCreationDateTime)}
            />
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          title="Member Info"
          defaultOpen={allExpanded}
          headerRight={
            <span className="text-sm text-gray-500">
              {formatEntityName(member as EdiEntity)}
            </span>
          }
        >
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            }}
          >
            <Field label="Name" value={formatEntityName(member as EdiEntity)} />
            <Field
              label="Member ID"
              value={member?.identifier || coverage.identifier}
            />
            <Field
              label="Birth Date"
              value={formatDisplayDate(member?.birthDate)}
            />
            <Field label="Gender" value={member?.gender} />
            <Field label="SSN" value={member?.ssn} />
            <Field
              label="Address"
              value={formatAddress(member?.address || coverage.mailingAddress)}
            />
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Enrollment Status" defaultOpen={allExpanded}>
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            }}
          >
            <Field label="Member Indicator" value={coverage.memberIndicator} />
            <Field
              label="Relationship Code"
              value={coverage.relationshipCode}
            />
            <Field
              label="Maintenance Type"
              value={coverage.maintenanceTypeCode}
            />
            <Field
              label="Maintenance Reason"
              value={coverage.maintenanceReasonCode}
            />
            <Field label="Benefit Status" value={coverage.benefitStatusCode} />
            <Field
              label="Employment Status"
              value={coverage.employmentStatusCode}
            />
          </div>
        </CollapsibleSection>

        {coverage.sponsor && (
          <CollapsibleSection title="Sponsor" defaultOpen={allExpanded}>
            <div
              className="grid gap-4"
              style={{
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              }}
            >
              <Field label="Name" value={coverage.sponsor.lastNameOrOrgName} />
              <Field label="ID" value={coverage.sponsor.identifier} />
            </div>
          </CollapsibleSection>
        )}

        {coverage.insurer && (
          <CollapsibleSection title="Insurer" defaultOpen={allExpanded}>
            <div
              className="grid gap-4"
              style={{
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              }}
            >
              <Field label="Name" value={coverage.insurer.lastNameOrOrgName} />
              <Field label="ID" value={coverage.insurer.identifier} />
            </div>
          </CollapsibleSection>
        )}

        {coverage.coverages && coverage.coverages.length > 0 && (
          <CollapsibleSection title="Coverages" defaultOpen={allExpanded}>
            <div className="space-y-3">
              {coverage.coverages.map((cov, idx) => (
                <div key={idx} className="p-3 bg-white rounded border">
                  <div
                    className="grid gap-4"
                    style={{
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(150px, 1fr))",
                    }}
                  >
                    <Field
                      label="Plan Description"
                      value={cov.planCoverageDescription}
                    />
                    <Field
                      label="Coverage Level"
                      value={cov.coverageLevelCode}
                    />
                    <Field
                      label="Insurance Line"
                      value={cov.insuranceLineCode}
                    />
                    <Field
                      label="Benefit Begin"
                      value={formatDisplayDate(cov.benefitBeginDate)}
                    />
                    <Field
                      label="Benefit End"
                      value={formatDisplayDate(cov.benefitEndDate)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
};

export default MemberCoverage834Viewer;
